import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Wallet, CreditCard, X, Check } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { expensesApi } from '../../api/endpoints/expenses';
import { formatCurrency } from '../../lib/formatters';
import { MEMBERSHIP_PLANS, PAY_URL } from '../../lib/constants';

type Method = 'wallet' | 'card';

type CardSession = { sessionId: string; paymentUrl: string };

interface Props {
  storeId: string;
  storeName: string;
  plan: string;
  cycle?: 'monthly' | 'yearly';
  onClose: () => void;
  onSuccess?: () => void;
}

/**
 * Full-featured payment chooser modal for membership purchases — wallet vs.
 * card. Mirrors the legacy `purchaseStoreExpense()` popup in
 * views/rightMenu.ejs but using the v1 dashboard API. Embeds the existing
 * Moyasar card + Apple Pay iframe widgets (/widgets/card/:sessionId,
 * /widgets/applepay/:sessionId) and polls /session/:sessionId/status until
 * the payment lands.
 */
export function PlanPaymentModal({ storeId, storeName, plan, cycle = 'monthly', onClose, onSuccess }: Props) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const planConfig = MEMBERSHIP_PLANS[plan];
  const amount = (cycle === 'yearly' ? planConfig?.priceYearly : planConfig?.price) ?? 0;
  const planTitle = (i18n.language === 'ar' ? planConfig?.titleAr : planConfig?.titleEn) ?? plan;

  const [selected, setSelected] = useState<Method | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cardSession, setCardSession] = useState<CardSession | null>(null);
  const [succeeded, setSucceeded] = useState<Method | null>(null);

  const { data: balanceData, isLoading: balanceLoading } = useQuery({
    queryKey: ['expenses-balance', storeId],
    queryFn: () => expensesApi.getBalance(storeId),
  });
  const balance = balanceData?.balance ?? 0;
  const currency = balanceData?.currency ?? 'SAR';
  const canPayFromWallet = balance >= amount;

  const purchase = useMutation({
    mutationFn: (method: Method) =>
      expensesApi.purchaseMembership(storeId, plan, method === 'wallet' ? 'balance' : 'card', cycle),
    onSuccess: (res, method) => {
      if (res.paymentMethod === 'balance') {
        setSucceeded(method);
        queryClient.invalidateQueries({ queryKey: ['expenses-balance', storeId] });
        queryClient.invalidateQueries({ queryKey: ['membership-status', storeId] });
        queryClient.invalidateQueries({ queryKey: ['stores'] });
        onSuccess?.();
      } else {
        setCardSession({ sessionId: res.sessionId, paymentUrl: res.paymentUrl });
      }
    },
    onError: (err: unknown) => {
      const code = (err as { code?: string })?.code;
      if (code === 'insufficient_balance') {
        setError(t('expenses.insufficientBalance'));
      } else {
        setError(t('expenses.connectionError'));
      }
    },
  });

  // Poll the pay-session status every 3s once the card iframe is showing.
  // When `payed > 0` lands, swap to the success view.
  const pollRef = useRef<number | null>(null);
  useEffect(() => {
    if (!cardSession) return;
    let stopped = false;
    const tick = async () => {
      try {
        const status = await expensesApi.getSessionStatus(cardSession.sessionId);
        if (!stopped && status.payed > 0) {
          setSucceeded('card');
          queryClient.invalidateQueries({ queryKey: ['expenses-balance', storeId] });
          queryClient.invalidateQueries({ queryKey: ['membership-status', storeId] });
          queryClient.invalidateQueries({ queryKey: ['stores'] });
          onSuccess?.();
        }
      } catch {
        // swallow; the poll keeps retrying
      }
    };
    pollRef.current = window.setInterval(tick, 3000);
    return () => {
      stopped = true;
      if (pollRef.current) window.clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [cardSession, onSuccess, queryClient, storeId]);

  // --- Success view ---
  if (succeeded) {
    return (
      <Modal isOpen onClose={onClose} title={planTitle} size="sm">
        <div className="flex flex-col items-center py-8 text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success/15">
            <Check className="h-8 w-8 text-success" />
          </div>
          <h3 className="text-lg font-semibold">{t('expenses.paymentSuccess')}</h3>
          <p className="mt-1 text-sm text-text-muted">
            {succeeded === 'wallet'
              ? t('expenses.paymentSuccessWallet')
              : t('expenses.paymentSuccessCard')}
          </p>
          <Button variant="primary" onClick={onClose} className="mt-6">
            {t('expenses.close')}
          </Button>
        </div>
      </Modal>
    );
  }

  // --- Inline card-payment view (iframes + polling) ---
  if (cardSession) {
    return (
      <Modal isOpen onClose={onClose} title={planTitle} size="md">
        <div className="space-y-3">
          <iframe
            src={`${PAY_URL}/widgets/card/${cardSession.sessionId}`}
            sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
            className="w-full rounded-lg border border-border-default bg-white"
            style={{ height: 220 }}
          />
          <iframe
            src={`${PAY_URL}/widgets/applepay/${cardSession.sessionId}`}
            sandbox="allow-scripts allow-forms allow-same-origin allow-popups"
            className="w-full rounded-lg border border-border-default bg-white"
            style={{ height: 50 }}
          />
        </div>
      </Modal>
    );
  }

  // --- Chooser view ---
  const footer = (
    <>
      <Button variant="secondary" onClick={onClose} disabled={purchase.isPending}>
        <X className="h-4 w-4" />
        {t('common.cancel')}
      </Button>
      <Button
        variant="primary"
        disabled={!selected}
        isLoading={purchase.isPending}
        onClick={() => {
          setError(null);
          if (selected) purchase.mutate(selected);
        }}
      >
        {!selected
          ? t('expenses.selectPaymentFirst')
          : selected === 'wallet'
            ? t('expenses.payFromWallet')
            : t('expenses.continueToCard')}
      </Button>
    </>
  );

  return (
    <Modal isOpen onClose={onClose} title={planTitle} size="md" footer={footer}>
      <p className="mb-4 text-sm text-text-muted text-center">
        {storeName} · {t('expenses.choosePaymentMethod')}
      </p>

      {balanceLoading ? (
        <div className="flex items-center justify-center py-10">
          <LoadingSpinner size="md" />
        </div>
      ) : (
        <>
          <div className="mb-4 rounded-xl border border-border-default bg-bg-tertiary p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-muted">{t('expenses.requiredAmount')}</span>
              <span className="text-lg font-semibold text-accent">
                {formatCurrency(amount, currency, i18n.language)}
                <span className="ms-1 text-xs font-normal text-text-muted">
                  {i18n.language === 'ar' ? (cycle === 'yearly' ? '/ سنوياً' : '/ شهرياً') : (cycle === 'yearly' ? '/ yr' : '/ mo')}
                </span>
              </span>
            </div>
            <div className="mt-3 flex items-center justify-between border-t border-border-default pt-3">
              <span className="text-sm text-text-muted">{t('expenses.walletBalance')}</span>
              <span className={`text-sm font-medium ${canPayFromWallet ? 'text-success' : 'text-danger'}`}>
                {formatCurrency(balance, currency, i18n.language)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => canPayFromWallet && setSelected('wallet')}
              disabled={!canPayFromWallet}
              className={`relative flex flex-col items-center rounded-xl border p-4 transition-all ${
                selected === 'wallet'
                  ? 'border-accent bg-accent/10'
                  : canPayFromWallet
                    ? 'border-border-default bg-bg-tertiary hover:border-accent/50'
                    : 'cursor-not-allowed border-border-default bg-bg-tertiary opacity-50'
              }`}
            >
              {selected === 'wallet' && (
                <Check className="absolute top-2 end-2 h-4 w-4 text-accent" />
              )}
              <Wallet className="mb-2 h-8 w-8 text-text-secondary" />
              <span className="text-sm font-semibold">{t('expenses.wallet')}</span>
              <span className="mt-1 text-xs text-text-muted">
                {canPayFromWallet ? t('expenses.instantPayment') : t('expenses.insufficientBalance')}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setSelected('card')}
              className={`relative flex flex-col items-center rounded-xl border p-4 transition-all ${
                selected === 'card'
                  ? 'border-accent bg-accent/10'
                  : 'border-border-default bg-bg-tertiary hover:border-accent/50'
              }`}
            >
              {selected === 'card' && (
                <Check className="absolute top-2 end-2 h-4 w-4 text-accent" />
              )}
              <CreditCard className="mb-2 h-8 w-8 text-text-secondary" />
              <span className="text-sm font-semibold">{t('expenses.card')}</span>
              <span className="mt-1 text-xs text-text-muted">{t('expenses.cardSubtitle')}</span>
            </button>
          </div>

          {error && (
            <div className="mt-4 rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-center text-sm text-danger">
              {error}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
